import type { ExcelDownloadClient } from "app/components/React/ExcelButton/ExcelClientDownload";
import type {
    AppsExcelDownloadRequestDto,
    AppsSearchRequestDto,
    AppsSearchResponseDto,
} from "app/pages/sales-intelligence/modules/apps-search/types/dto";
import type {
    LeadsExcelDownloadRequestDto,
    LeadsSearchRequestDto,
    LeadsSearchResponseDto,
} from "app/pages/sales-intelligence/modules/leads-search";
import type {
    ContactsResponseDto,
    ContactsSearchResultsRequestDto,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import type { OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import type { IFetchService, PostConfigObject } from "app/services/fetchService";

import { createStaticListUrl } from "./utils";

function createStaticListsWebsitesApiService(fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) {
    const createLeadsPath = (listId: OpportunityListType["opportunityListId"], path?: string): string => {
        const baseListPath = createStaticListUrl(listId);

        return `${baseListPath}/websites${path ?? ""}`;
    };

    return {
        fetchSearchResults(
            listId: OpportunityListType["opportunityListId"],
            dto: LeadsSearchRequestDto,
            config?: PostConfigObject,
        ): Promise<LeadsSearchResponseDto> {
            return fetchService.post(createLeadsPath(listId), dto, config);
        },

        exportToExcel(listId: OpportunityListType["opportunityListId"], dto: LeadsExcelDownloadRequestDto) {
            return excelDownloadClient(createLeadsPath(listId, "/excel"), dto);
        },

        fetchContacts(
            listId: OpportunityListType["opportunityListId"],
            dto: ContactsSearchResultsRequestDto,
            config?: PostConfigObject,
        ): Promise<ContactsResponseDto> {
            return fetchService.post(createLeadsPath(listId, "/contacts"), dto, config);
        },
    };
}

function createStaticListsCompaniesApiService(fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) {
    const createLeadsPath = (listId: OpportunityListType["opportunityListId"], path?: string): string => {
        const baseListPath = createStaticListUrl(listId);

        return `${baseListPath}/companies${path ?? ""}`;
    };

    return {
        fetchSearchResults(
            listId: OpportunityListType["opportunityListId"],
            dto: LeadsSearchRequestDto,
            config?: PostConfigObject,
        ): Promise<LeadsSearchResponseDto> {
            return fetchService.post(createLeadsPath(listId), dto, config);
        },

        exportToExcel(listId: OpportunityListType["opportunityListId"], dto: LeadsExcelDownloadRequestDto) {
            return excelDownloadClient(createLeadsPath(listId, "/excel"), dto);
        },

        fetchContacts(
            listId: OpportunityListType["opportunityListId"],
            dto: ContactsSearchResultsRequestDto,
            config?: PostConfigObject,
        ): Promise<ContactsResponseDto> {
            return fetchService.post(createLeadsPath(listId, "/contacts"), dto, config);
        },
    };
}

function createStaticListsAppsApiService(fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) {
    const createAppsPath = (listId: OpportunityListType["opportunityListId"], path?: string): string => {
        const baseListPath = createStaticListUrl(listId);

        return `${baseListPath}/apps${path ?? ""}`;
    };

    return {
        fetchSearchResults(
            listId: OpportunityListType["opportunityListId"],
            dto: AppsSearchRequestDto,
            config?: PostConfigObject,
        ): Promise<AppsSearchResponseDto> {
            return fetchService.post(createAppsPath(listId), dto, config);
        },

        exportToExcel(listId: OpportunityListType["opportunityListId"], dto: AppsExcelDownloadRequestDto) {
            return excelDownloadClient(createAppsPath(listId, "/excel"), dto);
        },
    };
}

export function createStaticListsApiService(fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) {
    return {
        apps: createStaticListsAppsApiService(fetchService, excelDownloadClient),
        companies: createStaticListsCompaniesApiService(fetchService, excelDownloadClient),
        websites: createStaticListsWebsitesApiService(fetchService, excelDownloadClient),
    };
}
